import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import parseHtml from "html-react-parser";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { z } from "zod";
import type { AnswerFinderSourceView } from "../../../../skills/leadcue-reply/scripts/lib/answer-finder-contracts.ts";
import type { AnswerChoice } from "../../../../skills/leadcue-reply/scripts/lib/answer-scope.ts";
import { isConditionalNoStep } from "../../../../skills/leadcue-reply/scripts/lib/conditional-no.ts";
import {
  contextValueSchemas,
  isContextKey,
} from "../../../../skills/leadcue-reply/scripts/lib/context-contracts.ts";
import { leadLinkStepPlacement } from "../../../../skills/leadcue-reply/scripts/lib/lead-scheduling-link.ts";
import { isMaterialStep } from "../../../../skills/leadcue-reply/scripts/lib/materials.ts";
import { outsideGoalStepKind } from "../../../../skills/leadcue-reply/scripts/lib/outside-goal.ts";
import {
  dependenciesSatisfied,
  type PlanStep,
  type WorkPlan,
  WorkPlanSchema,
} from "../../../../skills/leadcue-reply/scripts/lib/work-plan.ts";
import {
  ACTIVITY_FILTERS,
  type ActivityEntry,
  type ActivityFilter,
  activityFilterLabel,
  activityView,
} from "../../src/activity-view.ts";
import {
  answerHint,
  appliesTo,
  campaignChoiceBlocked,
  saveLabel,
} from "../../src/answer-scope-view.ts";
import { appCount, appDateTime, appTime, viewerTimeZone } from "../../src/app-dates.ts";
import { approveBlocked } from "../../src/approve-gate.ts";
import {
  bookingStatusChoices,
  completionControls,
  openBookingQuestion,
} from "../../src/booking-question.ts";
import { BRIEF_REVIEW_KEYS } from "../../src/brief-contracts.ts";
import { CallLengthSchema } from "../../src/call-length-view.ts";
import { closedStepLabel, lateDraftRevisions, workFold } from "../../src/compose-box.ts";
import {
  deferredQuestionsOf,
  draftNotesFor,
  draftNotesOf,
  offerLineFor,
} from "../../src/draft-source-line.ts";
import type { EmailDisplay } from "../../src/email-display.ts";
import { StandingAnswerFieldsSchema } from "../../src/faq-catalog.ts";
import { goalLifecycle } from "../../src/goal-lifecycle.ts";
import { inFlightView } from "../../src/in-flight-view.ts";
import {
  answersMessageIdFor,
  commitmentHeading,
  declinedStatusFromEvents,
  draftFreshness,
  draftingOffNote,
  isDeveloperReviewStep,
  leadStepId,
  openConditionalNoStep,
  openLeadLinkStep,
  openOutsideGoalStep,
  statedOfferFromEvents,
  visibleSteps,
} from "../../src/inbox-view.ts";
import {
  cleanEmailHtml,
  type DraftRevision,
  type DraftRevisionSchema,
  draftEmail,
  type ThreadSnapshotSchema,
} from "../../src/mail-content.ts";
import {
  issuePlanCommand,
  type PlanCommand,
  type PlanCommandOutcome,
  planCommandPorts,
} from "../../src/plan-command.ts";
import type { RepReplyView } from "../../src/rep-reply.ts";
import type { SignOffMode, SignOffPreviews } from "../../src/sign-off-mode.ts";
import { withheldView } from "../../src/withheld-view.ts";
import { AnsweredCard, type StandingOutcome, StandingOutcomeSchema } from "../answered-card.tsx";
import { AppliesToField, AppliesToLine } from "../applies-to.tsx";
import {
  availabilityBody,
  availabilityComplete,
  type CardLength,
  emptyAvailability,
} from "../availability-slots.tsx";
import { OPEN_COMPOSE_EVENT } from "../compose.tsx";
import { ConditionalNoSheet, conditionHoldsRefusal } from "../conditional-no-sheet.tsx";
import {
  OpenQuestion,
  type Redraft,
  useQuestionClosed,
  useRedraftAsking,
  useWorkBusy,
} from "../draft-again.tsx";
import { DraftHintsPanel, useDraftHints } from "../draft-hints.tsx";
import { DraftMaterials } from "../draft-materials.tsx";
import { SlotsInPlace, useDraftReadiness } from "../draft-slots.tsx";
import {
  DeferredQuestionLineView,
  DraftNoteLine,
  OfferSourceLineView,
} from "../draft-source-line.tsx";
import { Segmented } from "../drafting-switches.tsx";
import { LinkButton, LinkLayer, useEditorLinks } from "../editor-links.tsx";
import {
  emptyDraft as emptyStandingDraft,
  complete as standingFieldsComplete,
  fieldsFor as standingFieldsFor,
} from "../faq.tsx";
import {
  BoldIcon,
  BookedIcon,
  CheckIcon,
  EditIcon,
  ExternalLinkIcon,
  ItalicIcon,
  ListIcon,
} from "../icons.tsx";
import { InFlightCard } from "../in-flight-card.tsx";
import { type ReplyBoxLink, useBoxContent } from "../inbox-v2/reply-box-link.ts";
import { LeadLinkTask } from "../lead-link-sheet.tsx";
import { MaterialRequestCard, openMaterialRequestStep } from "../material-request-sheet.tsx";
import { draftAnywayRefusal, OutsideGoalSheet } from "../outside-goal-sheet.tsx";
import { PhoneCallCard } from "../phone-call-card.tsx";
import { StepHeader } from "../replied-message.tsx";
import { SendControls } from "../send-controls.tsx";
import { WithheldCard } from "../withheld-card.tsx";
import {
  AppLink,
  answersPath,
  campaignPath,
  settingsSectionPath,
  usePlanApi,
} from "../workspace-context.tsx";
import { RescueCard, useAgentLine } from "./agent-quiet.tsx";
import { AnswerField, type Decision, type LengthChoice } from "./answer-field.tsx";
import {
  type BoxRelation,
  boxRelation,
  filesLeftBehind,
  filesLeftBehindNote,
} from "./approve-from-box.ts";
import { ClosedLine } from "./closed-line.tsx";
import {
  dispositionLabel,
  heldNote,
  parseEmail,
  qualifiable,
  SHOW_MESSAGE_EVENT,
  whenLabel,
} from "./conversation-shared.ts";
import { DetailSchema, type EventsSchema } from "./detail-schema.ts";
import { useHeldAtFoot } from "./held-at-foot.ts";
import { compactInboxDraft, InboxDraftLine } from "./inbox-draft-line.tsx";
import { request } from "./page-request.ts";
import {
  ApprovedReplyActions,
  approveLabel,
  stepApproveLabel,
  useStepApproval,
} from "./step-approval.tsx";

/** The held-draft card's reading of the conversation's events and the spans sent beside them. */
const heldView = (data: z.infer<typeof EventsSchema> | undefined) =>
  withheldView(data?.events ?? [], data?.refusedSpans ?? []);

const MeetingCompletionResponseSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal("confirmed"), plan: WorkPlanSchema }),
  z.object({ status: z.literal("unconfirmed"), reason: z.string() }),
]);

/*
 * A reply step's state names where the email is, because the send happens by hand in Bison: an
 * approved draft is not sent, and a reported send is the rep's word, not something Replies saw.
 */
function stepBadge(step: PlanStep): string {
  if (step.input.kind === "reply" && step.state === "approved") return "Approved · not sent";
  if (step.input.kind === "reply" && step.state === "reported") return "Reported sent";
  // Only a workspace granted send-on-approve can reach these three states for a reply.
  if (step.input.kind === "reply" && ["queued", "executing"].includes(step.state))
    return "Sending…";
  if (step.input.kind === "reply" && step.state === "completed") return "Sent";
  if (step.input.kind === "reply" && step.state === "uncertain") return "Check Bison";
  // An answered question says so here, once; the card below shows the answer itself.
  if (step.input.kind === "question" && step.state === "completed") return "Answered";
  // Only an approval can need re-approval; an older refresh demoted open questions and to-dos too.
  if (step.state === "invalidated" && !["reply", "connector"].includes(step.input.kind))
    return stepStateLabel.pending;
  return stepStateLabel[step.state];
}

/** A step's badge. A question whose run is closed says so, whatever state the plan stores for it. */
function StepBadge(props: { plan: WorkPlan; step: PlanStep; closed: string | null }) {
  const questionClosed = useQuestionClosed(props.plan, props.step);
  return (
    <span className="lcr-badge">
      {props.closed ?? (questionClosed ? "Closed" : stepBadge(props.step))}
    </span>
  );
}

/** Who must act on a step and whether they must. A closed question asks nothing of anyone. */
function StepDuty(props: { plan: WorkPlan; step: PlanStep }) {
  if (useQuestionClosed(props.plan, props.step)) return null;
  return (
    <p className="lcr-meta-text">
      {actorLabel[props.step.actor]} · {props.step.mandatory ? "Required" : "Optional"}
    </p>
  );
}

const stepStateLabel: Record<PlanStep["state"], string> = {
  pending: "To do",
  approved: "Approved",
  queued: "Queued",
  executing: "Running",
  uncertain: "Check result",
  completed: "Done",
  reported: "Done",
  skipped: "Skipped",
  invalidated: "Needs re-approval",
};
const actorLabel: Record<PlanStep["actor"], string> = {
  rep: "You",
  connector: "Automatic",
  system: "System",
};

function ReplyStepNotes({
  detail,
  step,
  planApi,
}: {
  detail: Pick<
    WorkPaneProps["detail"],
    "offerSources" | "drafts" | "deferredQuestions" | "draftNotes" | "plan"
  >;
  step: PlanStep;
  planApi: string;
}) {
  const { drafts, offerSources, plan } = detail;
  const deferredQuestions = deferredQuestionsOf({ deferredQuestions: detail.deferredQuestions });
  const draftNotes = draftNotesOf({ draftNotes: detail.draftNotes });
  return (
    <>
      {" "}
      {step.input.kind === "reply" &&
        (() => {
          const line = offerLineFor(offerSources, drafts, step.input.draftRevision);
          return line ? (
            <OfferSourceLineView planApi={planApi} line={line} when={whenLabel} />
          ) : null;
        })()}
      {step.input.kind === "reply" &&
        deferredQuestions.map((question) => (
          <DeferredQuestionLineView
            key={question.speechActId}
            question={question}
            campaignId={plan.campaignId}
          />
        ))}
      {step.input.kind === "reply" &&
        draftNotesFor(draftNotes, step.input.draftRevision).map((note) => (
          <DraftNoteLine key={note.text} note={note} />
        ))}
    </>
  );
}

/**
 * What the work half is given: the plan detail and what the conversation pane has already read for
 * it, in; what it changes in the pane, out. Fetching stays with the pane (`Conversation`, `main.tsx`).
 */
export interface WorkPaneProps {
  /** The conversation as `GET …/plans/:planId` answers it (`DetailSchema`). */
  readonly detail: z.infer<typeof DetailSchema>;
  readonly viewerId: string | null;
  /** The conversation's event record, which the pane reads (`GET …/events`). */
  readonly events: UseQueryResult<z.infer<typeof EventsSchema>>;
  /** The reply box's view, or null until it is read or where the server offers no box. */
  readonly reply: RepReplyView | null;
  /** Whether the rep is writing their own reply in the box: the open work waits on it. */
  readonly writing: boolean;
  /** The thread's messages, each prepared once for display (`useEmailDisplays`). */
  readonly displays: readonly EmailDisplay[];
  /** A new attempt being awaited, and what the panel shows meanwhile (`useRedraft`). */
  readonly redraft: Redraft;
  /** Whether a draft editor holds unsaved text. */
  readonly editing: boolean;
  readonly onChange: () => Promise<void>;
  readonly onError: (message: string) => void;
  readonly onEditing: (editing: boolean) => void;
  /** The master inbox's reply box, which owns the draft's text there; absent in the pane. */
  readonly box?: DraftBox | null;
  /** What the item adds to the agent's line, such as Show activity (`agent-item.tsx`). */
  readonly extra?: ReactNode;
}

/**
 * The inbox's draft connection. The line opens the composer, whose Send owns approval.
 * Legacy approval helpers retain their callback for callers outside the inbox.
 */
export interface DraftBox {
  /** Approves the draft as the box holds it: saved first where the box's words differ from it. */
  readonly approve: (step: PlanStep, draft: DraftRevision) => Promise<void>;
  /** The box itself, whose words, as they change, decide the line that says Approve saves them. */
  readonly link: ReplyBoxLink;
  /** Whether the box can send this conversation (`detail.sendOnApprove`): where the rep sends from. */
  readonly sends: boolean;
}

/** Reply approval is a separate control only outside the inbox composer. */
function showsApproval(
  step: PlanStep,
  _box: DraftBox | null | undefined,
  _sendOnApprove: boolean,
): boolean {
  const waiting = ["pending", "invalidated"].includes(step.state);
  if (step.input.kind === "reply") return false;
  return step.input.kind === "connector" && waiting;
}

/**
 * The reply agent's cards and commands, as the master inbox's thread lays them out
 * (`agent-item.tsx`), which folds Activity itself. It was the second half of `Conversation` in
 * `main.tsx` until step 8b of docs/2026-10-17-master-inbox-in-the-existing-ui-plan.md.
 */
export function WorkPane({
  detail,
  viewerId,
  events,
  reply,
  writing,
  displays,
  redraft,
  editing,
  onChange,
  onError: setError,
  onEditing: setEditing,
  box,
  extra,
}: WorkPaneProps) {
  const {
    plan,
    thread,
    drafts,
    locked,
    answerFinderSources,
    signOffOptions,
    sendOnApprove,
    hubSpotConnected,
    lateReply,
  } = detail;
  const planApi = usePlanApi(plan.id);
  const leadStep = leadStepId(plan);
  const [busy, setBusy] = useWorkBusy(plan.id);
  const [bookingCheck, setBookingCheck] = useState("");
  // A send is in its approve, hold or queue phase: the manual copy-and-report panel steps aside.
  const [sending, setSending] = useState(false);
  const [foldShown, setFoldShown] = useState(false);
  const late = lateDraftRevisions(drafts, reply?.answered ?? null);
  const fold = workFold(plan, reply?.answered ?? null, writing, late);
  const folded = new Set(fold.kind === "open" || foldShown ? [] : fold.stepIds);
  const closedLabel = (step: PlanStep): string | null =>
    reply?.answered && fold.kind === "answered" && fold.stepIds.includes(step.id)
      ? closedStepLabel(step, reply.answered, late)
      : null;
  const now = new Date();
  // One rule with the inbox and the server: a later stop, final no or booking closes the offer.
  // And one with the inbox row on the lead's yes: not stated on imported history or after a no.
  const offer =
    events.data && thread ? statedOfferFromEvents(plan, thread.messages, events.data.events) : null;
  // A lead's no is read here and settled in Email Bison; the rep is never asked to act on it.
  const declined =
    events.data && thread
      ? declinedStatusFromEvents(plan, thread.messages, events.data.events)
      : null;
  // A reply outside the campaign's goal is the rep's to handle: one sheet, no outcomes to choose.
  const outsideGoalStep = openOutsideGoalStep(plan);
  const outsideGoalKind = outsideGoalStep ? outsideGoalStepKind(outsideGoalStep) : null;
  // A lead who offered their own booking page: one task sheet, which carries its own Mark booked.
  const leadLinkStep = openLeadLinkStep(plan);
  const leadLinkPlacement = leadLinkStep ? leadLinkStepPlacement(leadLinkStep) : null;
  // A no with a named condition: one card, the rep decides whether it holds (`conditional-no.ts`).
  const conditionalNoStep = openConditionalNoStep(plan);
  // Materials (docs/2026-10-02-materials-and-references.md): a lead asked for something the campaign
  // has nothing saved for, so a card stands in for the draft; once answered, what the redraft did is
  // said here, since the card closes with its step. A draft that says a file is attached, or whose
  // commitment slots wait on the rep (`draft-slots.tsx`), cannot be approved until they are done.
  const materialStep = openMaterialRequestStep(plan);
  const [materialSaid, setMaterialSaid] = useState("");
  const readiness = useDraftReadiness(plan);
  const bookingOpen = offer !== null && offer.closedBy === null;
  const agreed = bookingOpen ? offer.agreedAt : null;
  const lifecycle = goalLifecycle(plan.goalKind);
  const reminder =
    agreed !== null &&
    lifecycle.reminderAfterMs !== null &&
    now.getTime() - Date.parse(agreed) >= lifecycle.reminderAfterMs;
  // A stop that belongs to Bison is never the rep's work, so it is not shown.
  const repSteps = visibleSteps(redraft.plan);
  // The rep answered or asked again and nothing has come back yet: say so, with the time it started.
  const flight = redraft.flight ?? inFlightView(plan, detail.inFlight, new Date());
  /** One piece of work against the plan: busy while it runs, its refusal said, the plan read again. */
  const run = async (work: () => Promise<unknown>): Promise<void> => {
    setBusy(true);
    setError("");
    try {
      await work();
      await onChange();
    } catch (error) {
      setError(error instanceof Error ? error.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  };
  const commandRequest = (stepId: string, action: string, note?: string): Promise<unknown> =>
    request(`${planApi}/commands`, {
      commandId: crypto.randomUUID(),
      expectedRevision: plan.revision,
      stepId,
      action,
      ...(action === "report" ? { note: note ?? "Rep reported completion in the provider." } : {}),
    });
  const command = (stepId: string, action: string, note?: string): Promise<void> =>
    run(() => commandRequest(stepId, action, note));
  const approval = useStepApproval({ run, commandRequest });
  /** One approve or execute command: applied, refused, or unknown (`src/plan-command.ts`). */
  const issueCommand = (command: PlanCommand): Promise<PlanCommandOutcome> =>
    issuePlanCommand(
      planCommandPorts(planApi, () => request(`${planApi}/events`)),
      command,
    );
  const readPlan = async (): Promise<WorkPlan> => DetailSchema.parse(await request(planApi)).plan;
  /** A new attempt of this turn asked from its own card, that card's stop lifted (`ops-draft-one.ts`). */
  const askForDraft = async (
    path: string,
    refusal: (code: string | null) => string,
  ): Promise<void> => {
    setBusy(true);
    setError("");
    try {
      const response = await fetch(`${planApi}/${path}`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        body: "{}",
      });
      if (!response.ok) {
        const failure = z
          .object({ error: z.string() })
          .safeParse(await response.json().catch(() => null));
        throw new Error(refusal(failure.success ? failure.data.error : null));
      }
      await onChange();
    } catch (error) {
      setError(error instanceof Error ? error.message : "Could not ask for a draft.");
    } finally {
      setBusy(false);
    }
  };
  /** "Draft anyway" lifts the outside-goal stop; "Yes: draft a reply on it" the conditional no. */
  const draftAnyway = (): Promise<void> => askForDraft("draft-anyway", draftAnywayRefusal);
  const conditionHolds = (): Promise<void> =>
    askForDraft("conditional-no/holds", conditionHoldsRefusal);
  const qualify = async (outcome: "qualified" | "not_qualified"): Promise<void> => {
    setBusy(true);
    setError("");
    try {
      await request(`${planApi}/qualification`, {
        decisionId: crypto.randomUUID(),
        outcome,
      });
      await onChange();
    } catch (error) {
      setError(error instanceof Error ? error.message : "Could not save qualification.");
    } finally {
      setBusy(false);
    }
  };
  /** Reopens the draft an outcome closed ("Reopen draft"); Done is what closes it (LEA-225). */
  const saveOutcome = async (outcome: "active"): Promise<boolean> => {
    setBusy(true);
    setError("");
    try {
      await request(`${planApi}/outcome`, {
        commandId: crypto.randomUUID(),
        expectedRevision: plan.revision,
        outcome,
      });
      await onChange();
      return true;
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not reopen the draft.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const booking = async (action: "mark_booked" | "undo_booked"): Promise<void> => {
    setBusy(true);
    setError("");
    try {
      await request(`${planApi}/commands`, {
        commandId: crypto.randomUUID(),
        expectedRevision: plan.revision,
        action,
      });
      await onChange();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save the booking.");
    } finally {
      setBusy(false);
    }
  };
  /*
   * The booking question's "not yet" (change 6): the server writes the explicit absence the question
   * asks for, and the drafting rule decides whether it answers it. "Booked" is Mark booked itself.
   */
  const answerBooking = async (stepId: string): Promise<void> => {
    setBusy(true);
    setError("");
    try {
      await request(`${planApi}/answers`, {
        commandId: crypto.randomUUID(),
        expectedRevision: plan.revision,
        stepId,
        bookingStatus: "none",
      });
      await onChange();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not save the answer.");
    } finally {
      setBusy(false);
    }
  };
  const checkBooking = async (): Promise<void> => {
    setBusy(true);
    setError("");
    setBookingCheck("");
    try {
      const result = MeetingCompletionResponseSchema.parse(
        await request(`${planApi}/meeting-completion`, {
          commandId: crypto.randomUUID(),
          expectedRevision: plan.revision,
        }),
      );
      if (result.status === "unconfirmed") {
        setBookingCheck(
          result.reason === "reauthorization_required"
            ? "Connect HubSpot on this workspace's settings page before checking."
            : "No single confirmed booking matches this recipient and thread yet.",
        );
        return;
      }
      await onChange();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Could not check the booking.");
    } finally {
      setBusy(false);
    }
  };
  /*
   * The completion card (change 6). With the booking question open it is the first card and the one
   * place to act: it asks in the goal's own words and offers the goal's outcomes as choices, and the
   * question's own text box is not drawn. A goal with no completion receipt is offered only "nothing
   * scheduled yet", never a completion. Otherwise the card appears only once the lead agreed; before
   * that, Mark booked waits under Choose another outcome (`completionControls`).
   */
  const bookingQuestion = openBookingQuestion(redraft.plan);
  const choices = bookingStatusChoices(plan.goalKind);
  const controls = completionControls(plan, offer, hubSpotConnected);
  const completionOffered = controls.markBooked === "card";
  const missingWord =
    lifecycle.completion.status === "available" ? lifecycle.completion.missing : null;
  const markBookedLabel = plan.goalKind === "meeting" ? "Mark booked" : "Mark registered";
  const checkHubSpotButton = controls.checkHubSpot && (
    <button
      type="button"
      className="lcr-small-button"
      disabled={busy}
      onClick={() => void checkBooking()}
    >
      Check HubSpot booking
    </button>
  );
  const bookingCard = controls.card !== null && (
    <article
      className="lcr-sheet lcr-booking"
      id={bookingQuestion ? `step-${bookingQuestion.id}` : undefined}
      data-reminder={reminder || undefined}
      data-asking={bookingQuestion ? true : undefined}
    >
      <h3>
        {bookingQuestion ? choices.question : agreed ? commitmentHeading(lifecycle) : "Booking"}
      </h3>
      {agreed && (
        <p className="lcr-booking-when">
          The lead agreed {whenLabel(agreed)}.
          {reminder && missingWord && (
            <strong>
              {" "}
              {Math.floor((now.getTime() - Date.parse(agreed)) / 86_400_000)} days and still{" "}
              {missingWord}. Check, then record it here or follow up in Bison.
            </strong>
          )}
        </p>
      )}
      <p>
        {bookingQuestion
          ? completionOffered && choices.completion
            ? `The draft waits for this answer. ${choices.completion} records it and holds later replies for you; ${choices.absence.toLowerCase()} lets the draft go ahead.`
            : `The draft waits for this answer. ${choices.absence} lets it go ahead; if something is scheduled, answer the lead in Bison.`
          : plan.goalKind === "meeting"
            ? controls.checkHubSpot
              ? "Once the meeting is booked, mark it here, or let HubSpot confirm it. Either way a later reply is held rather than answered with new times."
              : "Once the meeting is booked, mark it here. A later reply is then held rather than answered with new times."
            : "Once the lead has registered, mark it here. A later reply is then held rather than redrafted."}
      </p>
      {bookingQuestion && (
        <AppliesToLine
          rule={{
            kind: "fixed",
            reach: "conversation",
            reason: "A booking belongs to one conversation.",
          }}
          campaignName={thread?.campaignName ?? null}
        />
      )}
      <div className="lcr-actions">
        {completionOffered && (
          <button
            type="button"
            disabled={busy || editing}
            onClick={() => void booking("mark_booked")}
          >
            <BookedIcon size={16} />
            {bookingQuestion ? choices.completion : markBookedLabel}
          </button>
        )}
        {bookingQuestion && offer?.closedBy !== "booking" && (
          <button
            type="button"
            disabled={busy || editing}
            onClick={() => void answerBooking(bookingQuestion.id)}
          >
            {choices.absence}
          </button>
        )}
        {completionOffered && checkHubSpotButton}
      </div>
      {bookingCheck && <p className="lcr-meta-text">{bookingCheck}</p>}
    </article>
  );
  const lineArgs = (step: PlanStep) => ({
    step,
    draft: stepDraft(drafts, step),
    link: box?.link ?? null,
    stale: staleDraft(events.data?.events, step, thread),
    readiness: readiness(step),
  });
  const line = useAgentLine({
    plan,
    steps: repSteps.filter((step) => !folded.has(step.id)),
    args: lineArgs,
    markBooked: controls.markBooked === "other_outcomes",
    cards: [outsideGoalStep, leadLinkStep, conditionalNoStep],
    markBookedLabel,
    onBooked: () => void booking("mark_booked"),
    checkHubSpot: checkHubSpotButton,
    busy,
    editing,
    extra,
  });
  return (
    <section className="lcr-agent-work">
      {locked && (
        <p className="lcr-backfill-note">
          Read-only while this campaign's history imports. Editing opens when it finishes.
        </p>
      )}
      <fieldset className="lcr-lock" disabled={locked}>
        {plan.goalKind === "phone_call" && (
          <PhoneCallCard planApi={planApi} planRevision={plan.revision} />
        )}
        {flight && (
          <InFlightCard
            view={flight}
            when={whenLabel}
            canWrite={Boolean(reply && thread && !locked)}
            onWrite={() => window.dispatchEvent(new CustomEvent(OPEN_COMPOSE_EVENT))}
          />
        )}
        {declined && (
          <article className="lcr-sheet lcr-declined">
            <h3>{declined} · handled</h3>
            <p>
              Nothing for you to do. Stopping, unsubscribing or blocking this lead is Email Bison's
              job; the thread stays here to read.
            </p>
          </article>
        )}
        {outsideGoalStep && outsideGoalKind && (
          <OutsideGoalSheet
            kind={outsideGoalKind}
            quote={outsideGoalStep.sources[0]?.quote ?? null}
            detail={
              outsideGoalStep.input.kind === "manual" ? outsideGoalStep.input.instructions : null
            }
            busy={busy}
            onReply={() => window.dispatchEvent(new CustomEvent(OPEN_COMPOSE_EVENT))}
            onHandled={() => void command(outsideGoalStep.id, "report", "Handled outside email.")}
            onDraftAnyway={() => void draftAnyway()}
          />
        )}
        {conditionalNoStep && (
          <ConditionalNoSheet
            sentence={conditionalNoStep.sources[0]?.quote ?? null}
            busy={busy}
            onHolds={() => void conditionHolds()}
            onNo={() => void command(conditionalNoStep.id, "report", "It's a no.")}
          />
        )}
        {leadLinkPlacement && (
          <LeadLinkTask
            planId={plan.id}
            planRevision={plan.revision}
            placement={leadLinkPlacement}
            viewerId={viewerId}
            onChange={onChange}
            onError={setError}
            onReply={() => window.dispatchEvent(new CustomEvent(OPEN_COMPOSE_EVENT))}
          />
        )}
        {materialStep && (
          <MaterialRequestCard
            planId={plan.id}
            quote={materialStep.sources[0]?.quote ?? null}
            onChange={onChange}
            onSaid={setMaterialSaid}
            onReply={() => window.dispatchEvent(new CustomEvent(OPEN_COMPOSE_EVENT))}
          />
        )}
        {materialSaid && (
          <p className="lcr-meta-text" role="status">
            {materialSaid}
          </p>
        )}
        {plan.goalCompletion?.source === "rep_claim" ? (
          <article className="lcr-sheet lcr-outcome-review lcr-booked">
            <span className="lcr-badge lcr-badge-green">Goal complete</span>
            <h3>
              {plan.goalCompletion.kind === "meeting" ? "Marked booked" : "Marked registered"}
            </h3>
            <p>
              {plan.goalCompletion.actorId === viewerId ? "You" : "A rep"} marked this{" "}
              {plan.goalCompletion.kind === "meeting" ? "booked" : "registered"}{" "}
              {whenLabel(plan.goalCompletion.claimedAt)}. LeadCue stops proposing new times, and a
              later reply is held for you. This is a mark, not a calendar receipt, so no meeting
              details are shown.
            </p>
            <div className="lcr-actions">
              <button type="button" disabled={busy} onClick={() => void booking("undo_booked")}>
                Undo
              </button>
            </div>
          </article>
        ) : plan.goalCompletion ? (
          <article className="lcr-sheet lcr-outcome-review">
            <span className="lcr-badge">Goal complete</span>
            <h3>Meeting booked</h3>
            <p>
              HubSpot associates one confirmed meeting with the exact recipient who received the
              connected booking link.
            </p>
            <p className="lcr-meta-text">
              {appDateTime(plan.goalCompletion.startsAt)} · verified{" "}
              {appDateTime(plan.goalCompletion.observedAt)}
            </p>
          </article>
        ) : plan.operatorOutcome ? (
          <ClosedLine
            outcome={plan.operatorOutcome}
            doneClosure={detail.doneClosure}
            viewerId={viewerId}
            bisonUrl={thread?.bisonUrl ?? null}
            busy={busy}
            onReopen={() => void saveOutcome("active")}
          >
            {extra}
          </ClosedLine>
        ) : (
          <>
            {fold.kind !== "open" && (
              <article className="lcr-sheet lcr-turn-fold" data-fold={fold.kind}>
                <h3>
                  {fold.kind === "paused"
                    ? "Paused while you write your own reply"
                    : fold.author === "rep"
                      ? `Answered by your reply · ${whenLabel(fold.at)}`
                      : `Answered in Bison · ${whenLabel(fold.at)}`}
                </h3>
                <p>
                  {fold.kind === "paused"
                    ? `${fold.summary}. They close once you tell us your reply went out in Bison.`
                    : fold.summary
                      ? `${fold.summary} closed, not deleted.`
                      : "Nothing was waiting on this message."}
                </p>
                {fold.kind === "answered" && fold.author === "rep" && (
                  <p className="lcr-meta-text">
                    {fold.seenAt
                      ? `Seen in Bison ${whenLabel(fold.seenAt)}`
                      : "Not yet seen in Bison. The thread shows your copy until it is."}
                  </p>
                )}
                {fold.stepIds.length > 0 && (
                  <button
                    type="button"
                    className="lcr-small-button lcr-ghost"
                    aria-expanded={foldShown}
                    onClick={() => setFoldShown(!foldShown)}
                  >
                    {foldShown
                      ? "Hide them"
                      : fold.kind === "paused"
                        ? "Show what it will close"
                        : "Show what it closed"}
                  </button>
                )}
              </article>
            )}
            {plan.disposition !== "active" &&
              !(plan.disposition === "answered_by_seller" && fold.kind === "answered") && (
                <article className="lcr-sheet">
                  {/* The header already names the switch; the sheet names the group it sits in. */}
                  <h3>
                    {plan.disposition === "drafting_off"
                      ? "Held"
                      : dispositionLabel[plan.disposition]}
                  </h3>
                  <p>
                    {plan.disposition === "drafting_off"
                      ? draftingOffNote(plan.draftingHeldBy)
                      : heldNote[plan.disposition]}
                  </p>
                  {plan.disposition === "drafting_off" && thread?.bisonUrl && (
                    <div className="lcr-actions">
                      <a href={thread.bisonUrl} target="_blank" rel="noreferrer">
                        <ExternalLinkIcon size={16} /> Open Bison
                      </a>
                    </div>
                  )}
                  {qualifiable.has(plan.disposition) && (
                    <div className="lcr-actions">
                      {plan.disposition !== "ignored_not_qualified" && (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => void qualify("not_qualified")}
                        >
                          Mark not qualified
                        </button>
                      )}
                      <button
                        className="lcr-primary"
                        type="button"
                        disabled={busy}
                        onClick={() => void qualify("qualified")}
                      >
                        Mark qualified
                      </button>
                    </div>
                  )}
                </article>
              )}
            {bookingQuestion && !folded.has(bookingQuestion.id) && bookingCard}
            {repSteps.map((step) =>
              step.id === bookingQuestion?.id ||
              folded.has(step.id) ||
              outsideGoalStepKind(step) !== null ||
              leadLinkStepPlacement(step) !== null ||
              isConditionalNoStep(step) ||
              isMaterialStep(step) ? null : isDeveloperReviewStep(step) &&
                ["pending", "invalidated"].includes(step.state) ? (
                // The run withheld its draft: say why and offer the reply box, never a bare
                // "Mark complete" (docs/2026-10-05-withheld-card.md).
                <WithheldCard
                  key={step.id}
                  stepId={step.id}
                  view={heldView(events.data)}
                  canWrite={Boolean(reply && thread && !locked)}
                  onWrite={() => window.dispatchEvent(new CustomEvent(OPEN_COMPOSE_EVENT))}
                />
              ) : (
                (compactInboxDraft({ ...lineArgs(step), actions: line.actionsFor(step.id) }) ?? (
                  <article className="lcr-sheet" key={step.id} id={`step-${step.id}`}>
                    <div className="lcr-step-heading">
                      <h3>{step.title}</h3>
                      <StepBadge plan={plan} step={step} closed={closedLabel(step)} />
                    </div>
                    {/*
                     * Who must act and whether they must is a to-do's line; an answer is past
                     * it, and a required reply says both in its title and its badge.
                     */}
                    {!(step.input.kind === "question" && step.state === "completed") &&
                      !(step.input.kind === "reply" && step.mandatory) && (
                        <StepDuty plan={plan} step={step} />
                      )}
                    <StepHeader
                      step={step}
                      messages={thread?.messages ?? []}
                      displays={displays}
                      events={events.data?.events ?? null}
                      late={lateReply}
                      when={whenLabel}
                      parse={parseEmail}
                      now={new Date()}
                    />
                    <SlotsInPlace {...{ plan, step, locked, onChange, readiness }}>
                      <StepBody
                        plan={plan}
                        step={step}
                        answerFinderSources={answerFinderSources}
                        campaignName={thread?.campaignName ?? null}
                        draft={stepDraft(drafts, step)}
                        signOffOptions={signOffOptions}
                        onChange={onChange}
                        onDirty={setEditing}
                        locked={locked}
                        redrafting={redraft.flight !== null}
                        box={box}
                      />
                    </SlotsInPlace>
                    <ReplyStepNotes detail={detail} step={step} planApi={planApi} />
                    {step.input.kind === "reply" &&
                      ["pending", "invalidated", "approved"].includes(step.state) && (
                        <DraftMaterials
                          planId={plan.id}
                          draftRevision={step.input.draftRevision}
                          onChange={onChange}
                        />
                      )}
                    {step.input.kind === "connector" && step.state === "approved" && (
                      <button
                        type="button"
                        className={step.id === leadStep ? "lcr-primary" : undefined}
                        disabled={busy || editing}
                        onClick={() => void command(step.id, "execute")}
                      >
                        Execute approved step
                      </button>
                    )}
                    {!box && step.input.kind === "reply" && (
                      <SendControls
                        plan={plan}
                        step={step}
                        thread={thread}
                        drafts={drafts}
                        blocked={approveBlocked({
                          busy,
                          editing,
                          dependenciesSatisfied: dependenciesSatisfied(plan, step),
                          staleDraft: staleDraft(events.data?.events, step, thread),
                          ...readiness(step),
                        })}
                        isLead={step.id === leadStep}
                        issue={issueCommand}
                        readPlan={readPlan}
                        refresh={onChange}
                        approveOnly={() => void command(step.id, "approve")}
                        onSending={setSending}
                        whenLabel={whenLabel}
                      />
                    )}
                    {showsApproval(step, box, sendOnApprove) && (
                      <ApproveStep
                        plan={plan}
                        step={step}
                        lead={step.id === leadStep}
                        blocked={approveBlocked({
                          busy,
                          editing,
                          dependenciesSatisfied: dependenciesSatisfied(plan, step),
                          staleDraft:
                            step.input.kind === "reply" &&
                            staleDraft(events.data?.events, step, thread),
                          ...readiness(step),
                        })}
                        draft={stepDraft(drafts, step)}
                        box={box ?? null}
                        onApprove={(approve) => approval.approve(step, approve)}
                      />
                    )}
                    {!box && (
                      <ApprovedReplyActions
                        plan={plan}
                        step={step}
                        sending={sending}
                        bisonUrl={thread?.bisonUrl ?? null}
                        onReport={() => void command(step.id, "report")}
                      />
                    )}
                    {step.input.kind === "reply" && step.state === "reported" && (
                      <SentLine plan={plan} step={step} viewerId={viewerId} />
                    )}
                    {["manual", "handoff", "handoff_artifact"].includes(step.input.kind) &&
                      ["pending", "invalidated"].includes(step.state) && (
                        <button
                          type="button"
                          disabled={busy || editing || !dependenciesSatisfied(plan, step)}
                          onClick={() => void command(step.id, "report")}
                        >
                          Mark complete
                        </button>
                      )}
                  </article>
                ))
              ),
            )}
            {!outsideGoalStep &&
              !leadLinkStep &&
              !conditionalNoStep &&
              !bookingQuestion &&
              bookingCard}
          </>
        )}
        {line.standalone}
        <RescueCard rescue={line.rescue} {...{ plan, busy, editing, onChange }} />
      </fieldset>
    </section>
  );
}

type Thread = z.infer<typeof ThreadSnapshotSchema>;
type StoredEvents = z.infer<typeof EventsSchema>["events"];

/** The draft a reply step shows, or undefined for another step or a draft not read yet. */
function stepDraft(drafts: readonly DraftRevision[], step: PlanStep): DraftRevision | undefined {
  const input = step.input;
  return input.kind === "reply"
    ? drafts.find((draft) => draft.revision === input.draftRevision)
    : undefined;
}

/** The same judgment the server makes before it takes an approval (`app.ts`, commands route). */
function staleDraft(events: StoredEvents | undefined, step: PlanStep, thread: Thread | null) {
  if (!events || step.input.kind !== "reply") return false;
  return (
    draftFreshness(answersMessageIdFor(events, step.input.draftRevision), thread?.messages ?? [])
      .kind === "stale"
  );
}

/** A reported send is the rep's word; Replies cannot read Bison's sent folder yet. */
function SentLine({
  plan,
  step,
  viewerId,
}: {
  plan: WorkPlan;
  step: PlanStep;
  viewerId: string | null;
}) {
  if (step.input.kind !== "reply") return null;
  const revision = step.input.draftRevision;
  const report = plan.sentRevisions.findLast((entry) => entry.draftRevision === revision);
  if (!report) return null;
  const who = report.actorId === viewerId ? "you" : "a rep";
  return (
    <p className="lcr-meta-text lcr-sent-line">
      Reported sent by {who}, {whenLabel(report.reportedAt)}. LeadCue does not see Bison's sent
      folder yet, so this is the report, not a delivery check.
    </p>
  );
}

/**
 * Everything that happened in this conversation, from its event record, plus what comes next from
 * the plan. The rows are decided by `activityView`; this only renders them and follows evidence.
 */
export function Activity({
  plan,
  drafts,
  viewerId,
  events,
}: {
  plan: WorkPlan;
  drafts: z.infer<typeof DraftRevisionSchema>[];
  viewerId: string | null;
  events: UseQueryResult<z.infer<typeof EventsSchema>>;
}) {
  const [filter, setFilter] = useState<ActivityFilter>("all");
  const [openSystem, setOpenSystem] = useState<string | null>(null);
  const [openDraft, setOpenDraft] = useState<number | null>(null);
  const [openReply, setOpenReply] = useState<string | null>(null);
  if (events.isPending) return <p className="lcr-activity-note">Loading activity…</p>;
  if (events.isError)
    return (
      <p className="lcr-activity-note" role="alert">
        Activity could not be loaded. The conversation itself is unaffected; reopen it to try again.
      </p>
    );
  const view = activityView({
    events: events.data.events,
    forcedDrafts: events.data.forcedDrafts,
    redraftLifts: events.data.redraftLifts,
    plainRedrafts: events.data.plainRedrafts,
    repReplies: events.data.repReplies,
    doneDecisions: events.data.doneDecisions,
    plan,
    filter,
    viewerId,
    timeZone: viewerTimeZone(),
    now: new Date(),
  });
  // The conversation opens a folded message before scrolling to it, so Activity asks it to.
  const showMessage = (messageId: string): void => {
    window.dispatchEvent(new CustomEvent(SHOW_MESSAGE_EVENT, { detail: messageId }));
  };
  const entry = (row: ActivityEntry, nested = false) => {
    const evidence = row.evidence;
    const draft =
      evidence?.kind === "draft"
        ? drafts.find((candidate) => candidate.revision === evidence.revision)
        : undefined;
    return (
      <li
        className="lcr-tl-row"
        data-tone={row.tone}
        data-nested={nested || undefined}
        key={row.key}
      >
        <time className="lcr-tl-time" dateTime={row.at}>
          {row.time}
        </time>
        <span className="lcr-tl-dot" aria-hidden="true" />
        <p className="lcr-tl-what">
          <b>{row.lead}</b>
          {row.rest && !row.rest.startsWith(":") ? " " : ""}
          {row.rest}
        </p>
        <span className="lcr-tl-who">{row.who}</span>
        {(evidence || draft) && (
          <div className="lcr-tl-actions">
            {evidence?.kind === "message" && (
              <button
                type="button"
                className="lcr-tl-link"
                onClick={() => showMessage(evidence.messageId)}
              >
                Show message
              </button>
            )}
            {draft && (
              <button
                type="button"
                className="lcr-tl-link"
                aria-expanded={openDraft === draft.revision}
                onClick={() => setOpenDraft(openDraft === draft.revision ? null : draft.revision)}
              >
                {openDraft === draft.revision ? "Hide this version" : "Show this version"}
              </button>
            )}
            {evidence?.kind === "receipt" && (
              <span className="lcr-tl-receipt">
                Receipt <code>{evidence.ref}</code>
              </span>
            )}
            {evidence?.kind === "reply_text" && (
              <button
                type="button"
                className="lcr-tl-link"
                aria-expanded={openReply === row.key}
                onClick={() => setOpenReply(openReply === row.key ? null : row.key)}
              >
                {openReply === row.key ? "Hide the reply" : "Show the reply"}
              </button>
            )}
            {evidence?.kind === "reply_text" && openReply === row.key && (
              <div className="lcr-tl-draft">{parseHtml(cleanEmailHtml(evidence.html))}</div>
            )}
            {draft && openDraft === draft.revision && (
              <div className="lcr-tl-draft">{parseHtml(cleanEmailHtml(draft.html))}</div>
            )}
          </div>
        )}
      </li>
    );
  };
  return (
    <section className="lcr-activity" aria-label="Activity">
      <fieldset className="lcr-activity-filters" aria-label="Show activity from">
        {ACTIVITY_FILTERS.map((candidate) => (
          <button
            key={candidate}
            type="button"
            className="lcr-activity-filter"
            aria-pressed={filter === candidate}
            onClick={() => setFilter(candidate)}
          >
            {activityFilterLabel[candidate]}
            <span className="lcr-activity-count">{view.counts[candidate]}</span>
          </button>
        ))}
      </fieldset>
      {view.rows.length === 0 ? (
        <p className="lcr-activity-note">
          {filter === "all"
            ? "Nothing has been recorded for this conversation yet."
            : `Nothing from ${activityFilterLabel[filter].toLowerCase()} in this conversation.`}
        </p>
      ) : (
        <ol className="lcr-timeline">
          {view.rows.map((row) => {
            if (row.type === "day")
              return (
                <li className="lcr-tl-day" key={row.key}>
                  {row.label}
                </li>
              );
            if (row.type === "event") return entry(row);
            if (row.type === "next")
              return (
                <li className="lcr-tl-row lcr-tl-next" key={row.key}>
                  <span className="lcr-tl-time">next</span>
                  <span className="lcr-tl-dot" aria-hidden="true" />
                  <p className="lcr-tl-what">{row.steps.join(" · ")}</p>
                  <span />
                </li>
              );
            const open = openSystem === row.key;
            return (
              <li className="lcr-tl-system" key={row.key}>
                <button
                  type="button"
                  className="lcr-tl-link"
                  aria-expanded={open}
                  onClick={() => setOpenSystem(open ? null : row.key)}
                >
                  {row.entries.length === 1
                    ? "1 system update"
                    : `${row.entries.length} system updates`}
                </button>
                {open && <ol>{row.entries.map((nested) => entry(nested, true))}</ol>}
              </li>
            );
          })}
        </ol>
      )}
    </section>
  );
}

/**
 * How this one draft ends. The rep sees the ending itself, not a description of it, and changing it
 * writes a new revision whose body is carried forward untouched — so their own edits are safe, and
 * the approval falls away exactly as it does for any other revision.
 */
function DraftEnding({
  draft,
  plan,
  step,
  options,
  disabled,
  onSaved,
}: {
  draft: DraftRevision;
  plan: WorkPlan;
  step: PlanStep;
  options: SignOffPreviews | null;
  disabled: boolean;
  onSaved: () => Promise<void>;
}) {
  const planApi = usePlanApi(plan.id);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // A revision written before endings were stored apart keeps its ending inside its body.
  if (draft.signOff === null) return null;
  const current = draft.signOff;
  const choose = async (mode: SignOffMode): Promise<void> => {
    setBusy(true);
    setError("");
    try {
      await request(`${planApi}/sign-off`, {
        commandId: crypto.randomUUID(),
        expectedRevision: plan.revision,
        stepId: step.id,
        mode,
      });
      setOpen(false);
      await onSaved();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The ending did not change.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="lcr-ending">
      <div className="lcr-ending-line">
        <span className="lcr-ending-current">
          {current.plainText ? `Ends: ${current.plainText.replace(/\n/g, " ")}` : "No ending"}
        </span>
        <button
          type="button"
          className="lcr-small-button"
          aria-expanded={open}
          disabled={disabled || busy}
          onClick={() => setOpen(!open)}
        >
          {open ? "Keep this ending" : "Change ending"}
        </button>
      </div>
      {disabled && <p className="lcr-drafting-note">Saving your edit first.</p>}
      {open && (
        <div className="lcr-ending-choices">
          <Segmented
            label="How this draft ends"
            value={current.mode}
            options={[
              { value: "first_name", label: "Best, first name" },
              { value: "none", label: "None" },
              {
                value: "full_signature",
                label: "Full signature",
                unavailable: options !== null && options.full_signature === null,
              },
            ]}
            disabled={busy}
            onChange={(mode) => void choose(mode)}
          />
          <p className="lcr-drafting-note">
            This draft only. The campaign keeps its own setting, and your edits to the message stay
            as they are.
          </p>
        </div>
      )}
      {error && <p role="alert">{error}</p>}
    </div>
  );
}

/** What Approve does with the reply box, said where the box holds something else (`boxRelation`). */
const BOX_NOTES: Record<Exclude<BoxRelation, "none">, string> = {
  edits: "Approve saves what your reply box holds as this draft, then approves it.",
  own: "Your reply box holds your own words; Approve approves the agent's draft as shown.",
  earlier: "Your reply box holds an earlier draft; Approve approves the agent's draft as shown.",
};

/**
 * Where the reply goes once approved (step 10, round 3): approving here never sends (`showsApproval`),
 * so the line says so before the click, and where the rep sends it from.
 */
const sendsFrom = () => "Nothing is sent: you send it from the reply box.";

/**
 * The line under Approve: the question it waits on, or what Approve does with the box's text, and,
 * for a reply, that it sends nothing (`sends`; null for another step).
 */
function ApproveNote(props: {
  waiting: boolean;
  box: BoxRelation;
  /** The box's files Approve leaves behind, said with the way to take them (`filesLeftBehindNote`). */
  files: string;
  sends: string | null;
}) {
  if (props.waiting) return <p className="lcr-meta-text">Answer the open question above first.</p>;
  const box = props.box === "none" ? [] : [BOX_NOTES[props.box]];
  const lines = [...box, props.files, props.sends ?? ""].filter(Boolean);
  const said = lines.join(" ").trim();
  return said ? <p className="lcr-meta-text">{said}</p> : null;
}

/**
 * A reply's Approve in the master inbox's thread: an edit of this draft in the box is what is
 * approved (`approve-from-box.ts`), and the line under it says what Approve does with the box and
 * that it sends nothing. "Edit in reply box" is the box's own (D8). The bar holds the pane's bottom
 * edge once the rep has scrolled into the draft (`held-at-foot.ts`), so a long draft never hides
 * Approve, and Approve never shows over draft text not yet on screen.
 */
function BoxApprove(props: {
  plan: WorkPlan;
  step: PlanStep;
  lead: boolean;
  blocked: boolean;
  draft: DraftRevision;
  box: DraftBox;
  onApprove: (approve: () => Promise<void>) => void;
}) {
  const { step, draft, box } = props;
  const content = useBoxContent(box.link);
  const relation = boxRelation(content, draft);
  const bar = useRef<HTMLDivElement>(null);
  const held = useHeldAtFoot(bar, relation);
  if (step.state === "approved" && relation !== "edits") return null;
  return (
    <div ref={bar} className="lcr-agent-approve" data-held={held || undefined}>
      <div className="lcr-actions lcr-agent-draft-actions">
        <button
          type="button"
          className={props.lead ? "lcr-primary" : undefined}
          data-shortcut={props.lead ? "approve" : undefined}
          disabled={props.blocked}
          onClick={() => props.onApprove(() => box.approve(step, draft))}
        >
          <CheckIcon size={16} /> {approveLabel(step.state === "approved")}
          {props.lead && <kbd className="lcr-kbd">A</kbd>}
        </button>
      </div>
      <ApproveNote
        waiting={!dependenciesSatisfied(props.plan, step)}
        box={relation}
        files={filesLeftBehindNote(filesLeftBehind(content, draft))}
        sends={sendsFrom()}
      />
    </div>
  );
}

/** Approve for an open reply or connector step; in the thread, a reply's goes through the box. */
function ApproveStep(props: {
  plan: WorkPlan;
  step: PlanStep;
  lead: boolean;
  blocked: boolean;
  draft: DraftRevision | undefined;
  box: DraftBox | null;
  /** Approve copies the approved reply too (`copiesOnApprove`). */
  /** The work Approve runs; none is the plan's own approve command. */
  onApprove: (approve?: () => Promise<void>) => void;
}) {
  const { step, draft, box } = props;
  if (box && draft && step.input.kind === "reply")
    return <BoxApprove {...props} draft={draft} box={box} onApprove={props.onApprove} />;
  return (
    <>
      <button
        type="button"
        className={props.lead ? "lcr-primary" : undefined}
        data-shortcut={props.lead ? "approve" : undefined}
        aria-describedby={step.input.kind === "reply" ? draftHintsId(step.id) : undefined}
        disabled={props.blocked}
        onClick={() => props.onApprove()}
      >
        <CheckIcon size={16} /> {stepApproveLabel(step)}
        {props.lead && <kbd className="lcr-kbd">A</kbd>}
      </button>
      <ApproveNote
        waiting={!dependenciesSatisfied(props.plan, step)}
        box="none"
        files=""
        sends={null}
      />
    </>
  );
}

/**
 * A reply step's draft. In the master inbox's thread it is only shown: the reply box owns its text
 * there, so the card never draws a second editor. In the pane, the draft editor, as before.
 */
function ReplyDraft(props: {
  plan: WorkPlan;
  step: PlanStep;
  draft: DraftRevision | undefined;
  signOffOptions: SignOffPreviews | null;
  onChange: () => Promise<void>;
  onDirty: (dirty: boolean) => void;
  locked: boolean;
  redrafting: boolean;
  box: DraftBox | null | undefined;
}) {
  const { draft, step } = props;
  // An approved draft reads as final. Editing it is a deliberate choice, because a saved edit
  // removes the approval; the button says so before the editor opens.
  const [editingApproved, setEditingApproved] = useState(false);
  if (!draft) return <p>Draft preparation is pending.</p>;
  const shown = parseHtml(cleanEmailHtml(draftEmail(draft).html));
  // A contenteditable is not a form control, so the locked fieldset cannot disable it.
  if (props.locked) return <div>{shown}</div>;
  if (props.box) return <InboxDraftLine draft={draft} link={props.box.link} />;
  if (step.state === "approved" && !editingApproved)
    return (
      <div className="lcr-approved-draft">
        <div className="lcr-approved-body">{shown}</div>
        <button type="button" className="lcr-small-button" onClick={() => setEditingApproved(true)}>
          <EditIcon size={16} /> Edit draft (removes approval)
        </button>
      </div>
    );
  return (
    <DraftEditor
      key={step.id}
      draft={draft}
      plan={props.plan}
      step={step}
      signOffOptions={props.signOffOptions}
      onSaved={props.onChange}
      onDirty={props.onDirty}
      awaited={props.redrafting}
    />
  );
}

function StepBody({
  plan,
  step,
  answerFinderSources,
  campaignName,
  draft,
  signOffOptions,
  onChange,
  onDirty,
  locked,
  redrafting,
  box,
}: {
  plan: WorkPlan;
  step: PlanStep;
  answerFinderSources: AnswerFinderSourceView[];
  campaignName: string | null;
  draft: DraftRevision | undefined;
  signOffOptions: SignOffPreviews | null;
  onChange: () => Promise<void>;
  onDirty: (dirty: boolean) => void;
  locked: boolean;
  /** A new draft is being written for this conversation (`useRedraft`): the editor is only kept. */
  redrafting: boolean;
  /** The master inbox's thread: the draft is shown, and its text is the reply box's. */
  box: DraftBox | null | undefined;
}) {
  // Kept here rather than in the form, which is replaced by the answered card once the plan reloads.
  const [standing, setStanding] = useState<readonly StandingOutcome[]>([]);
  switch (step.input.kind) {
    case "reply":
      return (
        <ReplyDraft
          {...{
            plan,
            step,
            draft,
            signOffOptions,
            onChange,
            onDirty,
            locked,
            redrafting,
            box,
          }}
        />
      );
    case "manual":
    case "handoff":
      return <p>{step.input.instructions}</p>;
    case "handoff_artifact":
      return (
        <div className="handoff-artifact">
          <p>{step.input.summary}</p>
          <p className="handoff-artifact-reason">{step.input.reason}</p>
          <p className="handoff-artifact-owner">
            For <strong>{step.input.responsible.name}</strong> · revision {step.revision} · hand
            over and mark complete yourself; nothing is sent from here.
          </p>
        </div>
      );
    case "question":
      if (step.state === "completed") {
        const saved = plan.history.findLast(
          (entry) => entry.stepId === step.id && entry.evidence.kind === "answer",
        );
        const values = saved?.evidence.kind === "answer" ? saved.evidence.values : {};
        return (
          <AnsweredCard
            questions={step.input.questions}
            values={values}
            campaignName={campaignName}
            standing={standing}
            answersHref={answersPath(plan.workspaceId)}
          />
        );
      }
      if (step.state !== "pending" && step.state !== "invalidated") return null;
      return (
        <OpenQuestion plan={plan} step={step}>
          <AnswerForm
            plan={plan}
            step={step}
            answerFinderSources={answerFinderSources}
            campaignName={campaignName}
            onStanding={setStanding}
            onSaved={onChange}
          />
        </OpenQuestion>
      );
    case "wait":
      return (
        <p>
          {step.input.reason} · {appDateTime(step.input.until)}
        </p>
      );
    case "connector":
      return <ConnectorSummary action={step.input.action} />;
  }
}

function ConnectorSummary({
  action,
}: {
  action: Extract<PlanStep["input"], { kind: "connector" }>["action"];
}) {
  switch (action.type) {
    case "meeting_create":
      return (
        <div className="lcr-action-detail">
          <p>
            Create <strong>{action.title}</strong> from {appDateTime(action.start)} to{" "}
            {appTime(action.end)}.
          </p>
          <p>Invite {action.attendees.join(", ")}.</p>
        </div>
      );
    case "unsubscribe":
      return <p>Unsubscribe this recipient from the connected campaign.</p>;
    case "block":
      return <p>Block {action.address} in the connected workspace.</p>;
  }
}

function standingOutcomes(response: unknown): StandingOutcome[] {
  const parsed = z
    .object({ standing: z.array(StandingOutcomeSchema).optional() })
    .safeParse(response);
  return parsed.success ? (parsed.data.standing ?? []) : [];
}
function withStanding(requests: readonly { key: string; entryId: string; scope: "campaign" }[]): {
  standing?: readonly { key: string; entryId: string; scope: "campaign" }[];
} {
  return requests.length > 0 ? { standing: requests } : {};
}
/**
 * The call length a typed slot is held to (`src/call-length.ts`), read only for a slots card:
 * `undefined` while it is read, and the rep's own choice when the read fails, since the save route
 * holds the slots to the server's length either way.
 */
function useCallLength(planApi: string, step: PlanStep, revision: number): CardLength {
  const asked =
    step.input.kind === "question" &&
    step.input.questions.length === 1 &&
    step.input.questions[0]?.key === "availability";
  const read = useQuery({
    queryKey: ["call-length", planApi, revision],
    enabled: asked,
    queryFn: async () => CallLengthSchema.parse(await request(`${planApi}/call-length`)),
  });
  return read.isError ? { kind: "choose" } : read.data;
}

function AnswerForm({
  plan,
  step,
  answerFinderSources,
  campaignName,
  onStanding,
  onSaved,
}: {
  plan: WorkPlan;
  step: Extract<PlanStep, { input: { kind: "question" } }> | PlanStep;
  answerFinderSources: AnswerFinderSourceView[];
  campaignName: string | null;
  /** What happened to each answer the rep also held for every lead, reported after saving. */
  onStanding: (outcome: readonly StandingOutcome[]) => void;
  onSaved: () => Promise<void>;
}) {
  const planApi = usePlanApi(plan.id);
  const decisionQuestion =
    step.input.kind === "question" &&
    step.input.questions.length === 1 &&
    step.input.questions[0]?.answerMode === "yes_no_check"
      ? step.input.questions[0]
      : null;
  const suggestedSource = decisionQuestion?.speechActId
    ? answerFinderSources.find(
        (source) =>
          source.questionId === decisionQuestion.speechActId &&
          source.messageId === decisionQuestion.answerFinderSuggestion?.source.messageId &&
          source.offsets.start === decisionQuestion.answerFinderSuggestion?.source.offsets.start &&
          source.offsets.end === decisionQuestion.answerFinderSuggestion?.source.offsets.end,
      )
    : undefined;
  const suggestedDecision = suggestedSource
    ? decisionQuestion?.answerFinderSuggestion?.decision
    : undefined;
  const [values, setValues] = useState<Record<string, string>>({});
  const [typedDraft, setTypedDraft] = useState(emptyStandingDraft);
  const [lengthChoice, setLengthChoice] = useState<LengthChoice>("minutes");
  const [availability, setAvailability] = useState(emptyAvailability);
  const callLength = useCallLength(planApi, step, plan.revision);
  const [decision, setDecision] = useState<Decision>(suggestedDecision ?? "");
  /*
   * Who each answer reaches, per question key, when the rep may choose (`appliesTo`). Unset means
   * the host's default for that key, which the card shows before saving.
   */
  const [choices, setChoices] = useState<Readonly<Record<string, AnswerChoice>>>({});
  // One Standing answer id per card, so a repeated submit revises its own row.
  const [standingEntry] = useState(() => `card_${crypto.randomUUID().replaceAll("-", "")}`);
  const [checker, setChecker] = useState("");
  const [followUpOn, setFollowUpOn] = useState("");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState<
    Array<{
      id: string;
      name: string;
      bytes: number;
      preview: string;
      extractedCharacters: number;
      previewTruncated: boolean;
    }>
  >([]);
  const [error, setError] = useState("");
  if (step.input.kind !== "question") return null;
  // A key's reach can depend on the question's wording: the reference question (materials step 6)
  // opens on "this campaign" though its key, `approved_facts`, defaults to this conversation.
  const questions = step.input.questions;
  const promptOf = (key: string): string | undefined =>
    questions.find((question) => question.key === key)?.prompt;
  /** Why "every later lead" cannot be chosen for this key right now, from what the rep supplied. */
  const blockedFor = (key: string) =>
    campaignChoiceBlocked(key, values[key] ?? "", uploads.length > 0);
  const choiceFor = (key: string): AnswerChoice => {
    if (blockedFor(key) !== null) return "thread";
    const chosen = choices[key];
    if (chosen) return chosen;
    // A question the conversation has moved past never defaults to telling every later lead.
    if (step.state === "invalidated") return "thread";
    const rule = appliesTo(key, promptOf(key));
    return rule?.kind === "choice" ? rule.default : "thread";
  };
  /** The answers the rep chose to hold for every lead in the campaign as well. */
  const standingFor = (keys: readonly string[]) => {
    const wide = keys.filter(
      (key) => appliesTo(key, promptOf(key))?.kind === "choice" && choiceFor(key) === "campaign",
    );
    return wide.map((key, index) => ({
      key,
      entryId: wide.length > 1 ? `${standingEntry}_${index}` : standingEntry,
      scope: "campaign" as const,
    }));
  };
  const appliesField = (key: string) => {
    const rule = appliesTo(key, promptOf(key));
    if (!rule) return null;
    return (
      <AppliesToField
        key={key}
        rule={rule}
        campaignName={campaignName}
        choice={choiceFor(key)}
        onChoice={(next) => setChoices((current) => ({ ...current, [key]: next }))}
        name={`${step.id}-${key}-applies`}
        heldIn={decisionQuestion ? "campaign_context" : "standing_answers"}
        campaignBlocked={blockedFor(key)}
        disabled={busy}
      />
    );
  };
  const onlyQuestion = step.input.questions.length === 1 ? step.input.questions[0] : null;
  const typedKey = onlyQuestion?.key;
  if (typedKey && ["campaign_goal", "next_step_policy", "calendar_mode"].includes(typedKey))
    return (
      <p>
        Set this on{" "}
        <AppLink href={campaignPath(plan.workspaceId, plan.campaignId)}>
          this campaign's page
        </AppLink>
        , then return to this conversation.
      </p>
    );
  if (
    typedKey &&
    ["seller_resources", "reply_guards", "answers_when_asked", "follow_up_promises"].includes(
      typedKey,
    )
  )
    return (
      <p>
        Add this structured fact in <a href={answersPath(plan.workspaceId)}>Standing answers</a>,
        then return to this conversation.
      </p>
    );
  const complete = decisionQuestion
    ? decision === "yes" ||
      decision === "no" ||
      (decision === "need_to_check" && checker.trim().length > 0 && followUpOn.length > 0)
    : typedKey === "meeting_duration_minutes"
      ? lengthChoice === "omit" ||
        (standingFieldsComplete("number", typedDraft) &&
          StandingAnswerFieldsSchema.safeParse(standingFieldsFor("number", typedDraft)).success)
      : typedKey === "scheduling_policy"
        ? standingFieldsComplete("scheduling", typedDraft) &&
          contextValueSchemas.rep_timezone.safeParse(typedDraft.hostTimezone).success &&
          StandingAnswerFieldsSchema.safeParse(standingFieldsFor("scheduling", typedDraft)).success
        : typedKey === "availability"
          ? availabilityComplete(availability, callLength)
          : step.input.questions.every((question) => {
              const value = values[question.key]?.trim();
              if (question.uploadAllowed && uploads.length > 0 && !value) return true;
              if (!isContextKey(question.key)) return false;
              return contextValueSchemas[question.key].safeParse(value).success;
            });
  // The button names who will be told, from the same rule as "Applies to" above it.
  const reachRule = onlyQuestion ? appliesTo(onlyQuestion.key, onlyQuestion.prompt) : null;
  const saveButtonLabel =
    (decisionQuestion && decision === ""
      ? null
      : typedKey === "meeting_duration_minutes" && lengthChoice === "omit"
        ? saveLabel("conversation")
        : reachRule?.kind === "choice"
          ? saveLabel(choiceFor(onlyQuestion?.key ?? ""))
          : reachRule?.kind === "fixed"
            ? saveLabel(reachRule.reach)
            : null) ?? (step.input.questions.length > 1 ? "Save answers" : "Save answer");
  return (
    <form
      className="lcr-answer"
      onSubmit={async (event) => {
        event.preventDefault();
        setBusy(true);
        setError("");
        try {
          const shared = {
            commandId: crypto.randomUUID(),
            expectedRevision: plan.revision,
            stepId: step.id,
          };
          const typedValues = Object.fromEntries(
            Object.entries(values).filter(([, value]) => value.trim().length > 0),
          );
          const saved = await request(
            `${planApi}/answers`,
            typedKey === "meeting_duration_minutes" && lengthChoice === "omit"
              ? { ...shared, lengthDisposition: "omit" }
              : typedKey === "meeting_duration_minutes" || typedKey === "scheduling_policy"
                ? {
                    ...shared,
                    typed: {
                      key: typedKey,
                      fields: standingFieldsFor(
                        typedKey === "meeting_duration_minutes" ? "number" : "scheduling",
                        typedDraft,
                      ),
                    },
                    ...withStanding(standingFor([typedKey])),
                  }
                : typedKey === "availability"
                  ? { ...shared, typed: availabilityBody(availability, callLength) }
                  : decisionQuestion && decision
                    ? {
                        ...shared,
                        decision:
                          decision === "need_to_check"
                            ? { kind: decision, checker: checker.trim(), followUpOn }
                            : { kind: decision, scope: choiceFor(decisionQuestion.key) },
                      }
                    : {
                        ...shared,
                        values: typedValues,
                        uploadIds: uploads.map((upload) => upload.id),
                        ...withStanding(standingFor(Object.keys(typedValues))),
                      },
          );
          onStanding(standingOutcomes(saved));
          await onSaved();
        } catch (cause) {
          if (
            cause instanceof Error &&
            cause.message.startsWith("This question was already answered.")
          )
            await onSaved();
          setError(cause instanceof Error ? cause.message : "Could not save this answer.");
        } finally {
          setBusy(false);
        }
      }}
    >
      {step.state === "invalidated" && (
        <p className="lcr-answer-stale">
          This was asked before the conversation or its campaign settings changed. Check that it
          still applies before you answer.
        </p>
      )}
      {decisionQuestion ? (
        <AnswerField
          kind="decision"
          idPrefix={step.id}
          prompt={decisionQuestion.prompt}
          suggestion={
            suggestedSource && suggestedDecision
              ? {
                  decision: suggestedDecision,
                  quote: suggestedSource.quote,
                  authoredAt: suggestedSource.authoredAt,
                }
              : null
          }
          value={{ decision, checker, followUpOn }}
          onChange={(next) => {
            setDecision(next.decision);
            setChecker(next.checker);
            setFollowUpOn(next.followUpOn);
          }}
        />
      ) : typedKey === "meeting_duration_minutes" ? (
        <AnswerField
          kind="duration"
          idPrefix={step.id}
          prompt={onlyQuestion?.prompt}
          value={{ choice: lengthChoice, draft: typedDraft }}
          disabled={busy}
          onChange={(change) =>
            "choice" in change
              ? setLengthChoice(change.choice)
              : setTypedDraft((current) => ({ ...current, ...change.fields }))
          }
        />
      ) : typedKey === "scheduling_policy" ? (
        <AnswerField
          kind="scheduling"
          idPrefix={step.id}
          value={typedDraft}
          disabled={busy}
          onChange={(next) => setTypedDraft((current) => ({ ...current, ...next }))}
        />
      ) : typedKey === "availability" ? (
        <AnswerField
          kind="availability"
          idPrefix={step.id}
          prompt={onlyQuestion?.prompt}
          value={availability}
          length={callLength}
          onChange={setAvailability}
        />
      ) : (
        questions.map((question) => (
          <AnswerField
            key={question.key}
            kind="text"
            name={question.key}
            idPrefix={`${step.id}-${question.key}`}
            prompt={question.prompt}
            value={values[question.key] ?? ""}
            required={!(question.uploadAllowed && uploads.length > 0)}
            error={answerHint(question.key, values[question.key] ?? "")}
            onChange={(next) => setValues((current) => ({ ...current, [question.key]: next }))}
          />
        ))
      )}
      {/* A Yes/No card has no answer field, so the brief pointer would describe one that is not there. */}
      {!decisionQuestion &&
        step.input.questions.some((question) => BRIEF_REVIEW_KEYS.has(question.key)) && (
          <p>
            For a brief or instructions document,{" "}
            <a href={settingsSectionPath(plan.workspaceId, "brief-import")}>
              import and review it in workspace settings
            </a>
            . Use this answer field for the specific fact requested.
          </p>
        )}
      {step.input.questions.some((question) => question.uploadAllowed) &&
        !step.input.questions.some((question) => BRIEF_REVIEW_KEYS.has(question.key)) && (
          <label>
            Add supporting files
            <input
              type="file"
              accept=".txt,.md,.pdf,.docx,.pptx"
              multiple
              disabled={busy || uploading || uploads.length >= 20}
              onChange={async (event) => {
                const files = [...(event.target.files ?? [])];
                if (!files.length) return;
                setUploading(true);
                setError("");
                try {
                  for (const file of files) {
                    const uploadId = crypto.randomUUID();
                    const extension = file.name.split(".").at(-1)?.toLowerCase();
                    const fallbackType =
                      extension === "md"
                        ? "text/markdown"
                        : extension === "txt"
                          ? "text/plain"
                          : extension === "pdf"
                            ? "application/pdf"
                            : extension === "docx"
                              ? "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                              : extension === "pptx"
                                ? "application/vnd.openxmlformats-officedocument.presentationml.presentation"
                                : "application/octet-stream";
                    const response = await fetch(`${planApi}/uploads`, {
                      method: "POST",
                      credentials: "same-origin",
                      headers: {
                        "content-type": file.type || fallbackType,
                        "x-leadcue-reply-upload-id": uploadId,
                        "x-leadcue-reply-step-id": step.id,
                        "x-leadcue-reply-file-name": encodeURIComponent(file.name),
                      },
                      body: file,
                    });
                    if (!response.ok) throw new Error(`Could not read ${file.name}.`);
                    const saved = z
                      .object({
                        upload: z.object({
                          id: z.string(),
                          name: z.string(),
                          bytes: z.number(),
                          preview: z.string(),
                          extractedCharacters: z.number(),
                          previewTruncated: z.boolean(),
                        }),
                      })
                      .parse(await response.json());
                    setUploads((current) => [...current, saved.upload]);
                  }
                } catch (cause) {
                  setError(cause instanceof Error ? cause.message : "Could not upload this file.");
                } finally {
                  setUploading(false);
                  event.target.value = "";
                }
              }}
            />
          </label>
        )}
      {uploads.length > 0 && (
        <ul className="lcr-upload-list">
          {uploads.map((upload) => (
            <li key={upload.id}>
              {upload.name} · {Math.ceil(upload.bytes / 1024)} KB
              {/*
                What was actually read out of the file. A scanned PDF extracts to nothing, and
                without this the rep submits an empty answer believing it carried their deck.
              */}
              {upload.extractedCharacters === 0 ? (
                <p className="lcr-upload-empty" role="alert">
                  No text could be read from this file. A scanned or image-only document cannot be
                  used as an answer.
                </p>
              ) : (
                <details>
                  <summary>
                    Read {appCount(upload.extractedCharacters)} characters — check this is the right
                    file
                  </summary>
                  <p className="lcr-upload-preview">
                    {upload.preview}
                    {upload.previewTruncated ? "…" : ""}
                  </p>
                </details>
              )}
            </li>
          ))}
        </ul>
      )}
      {decisionQuestion ? (
        decision === "need_to_check" ? (
          <AppliesToLine
            rule={{
              kind: "fixed",
              reach: "conversation",
              reason: "The promise is made to this lead only.",
            }}
            campaignName={campaignName}
          />
        ) : decision === "" ? null : (
          appliesField(decisionQuestion.key)
        )
      ) : typedKey === "meeting_duration_minutes" && lengthChoice === "omit" ? (
        <AppliesToLine
          rule={{
            kind: "fixed",
            reach: "conversation",
            reason: "Other leads are still asked for a length.",
          }}
          campaignName={campaignName}
        />
      ) : (
        questions.map((question) => appliesField(question.key))
      )}
      {error && <p role="alert">{error}</p>}
      <button className="lcr-primary" type="submit" disabled={busy || uploading || !complete}>
        {busy
          ? "Saving…"
          : uploading
            ? "Reading files…"
            : decision === "need_to_check"
              ? "Authorize holding reply"
              : suggestedSource && decision === suggestedDecision
                ? "Confirm suggested answer"
                : saveButtonLabel}
      </button>
    </form>
  );
}

/** Names a draft's "things to check" title, so its Approve button can point at it. */
const draftHintsId = (stepId: string): string => `lcr-hints-${stepId.replace(/[^\w-]/g, "_")}`;

function DraftEditor({
  draft,
  plan,
  step,
  signOffOptions,
  onSaved,
  onDirty,
  awaited,
}: {
  draft: DraftRevision;
  plan: WorkPlan;
  step: PlanStep;
  signOffOptions: SignOffPreviews | null;
  onSaved: () => Promise<void>;
  onDirty: (dirty: boolean) => void;
  /**
   * A new draft is being written over this one, as the conversation reads it (`useRedraft`): the
   * editor is drawn only because it holds unsaved text, and saves nothing into the draft replaced.
   */
  awaited: boolean;
}) {
  const planApi = usePlanApi(plan.id);
  const [status, setStatus] = useState("Saved");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const baseRevision = useRef(plan.revision);
  const seenDraft = useRef(draft.revision);
  const commandId = useRef(crypto.randomUUID());
  const latest = useRef(draft.html);
  const editor = useEditor({
    extensions: [StarterKit.configure({ link: { openOnClick: false } })],
    content: draft.html,
    editorProps: { attributes: { "aria-label": "Draft reply" } },
    onUpdate: ({ editor }) => {
      latest.current = editor.getHTML();
      setDirty(true);
      onDirty(true);
      setFailed(false);
      commandId.current = crypto.randomUUID();
      setStatus("Unsaved changes");
    },
  });
  const links = useEditorLinks(editor);
  const hints = useDraftHints(editor, draft.signOff);
  // While "Draft again" is asked this draft may be replaced: an edit made now would be lost unseen.
  const held = useRedraftAsking(plan.id);
  // An editor that goes away gives the conversation its editing state back, unsaved or not.
  const reportDirty = useRef(onDirty);
  reportDirty.current = onDirty;
  useEffect(() => () => reportDirty.current(false), []);
  // Its commands run on a locked editor too, so the toolbar waits out a save (found defect 56).
  const frozen = held || saving;
  useEffect(() => {
    // Without the second argument the editor reports the switch as an edit.
    editor?.setEditable(!held, false);
  }, [editor, held]);
  const save = async (): Promise<void> => {
    setSaving(true);
    // Without the second argument the editor reports each switch as an edit (found defect 54).
    editor?.setEditable(false, false);
    try {
      await request(`${planApi}/draft`, {
        commandId: commandId.current,
        expectedRevision: baseRevision.current,
        stepId: step.id,
        html: latest.current,
      });
      setDirty(false);
      onDirty(false);
      setStatus("Saved");
      await onSaved();
    } catch (error) {
      setFailed(true);
      setStatus(
        error instanceof Error ? error.message : "Could not save. Your changes are still here.",
      );
    } finally {
      setSaving(false);
      editor?.setEditable(true, false);
    }
  };
  useEffect(() => {
    if (!dirty || saving || failed || awaited) return;
    const timer = setTimeout(() => {
      void save();
    }, 1200);
    return () => clearTimeout(timer);
  });
  useEffect(() => {
    if (draft.revision === seenDraft.current || !editor) return;
    if (dirty) {
      setFailed(true);
      setStatus(
        "A newer draft is available. Your unsaved edits are still here; copy them before refreshing.",
      );
      return;
    }
    seenDraft.current = draft.revision;
    baseRevision.current = plan.revision;
    latest.current = draft.html;
    editor.commands.setContent(draft.html, { emitUpdate: false });
  }, [draft.revision, draft.html, plan.revision, dirty, editor]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent): void => {
      if (dirty) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);
  if (!editor) return null;
  return (
    <>
      <div className="lcr-compose">
        <div className="lcr-format-toolbar" role="toolbar" aria-label="Email formatting">
          <button
            type="button"
            aria-label="Bold"
            disabled={frozen}
            aria-pressed={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <BoldIcon size={16} />
          </button>
          <button
            type="button"
            aria-label="Italic"
            disabled={frozen}
            aria-pressed={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <ItalicIcon size={16} />
          </button>
          <button
            type="button"
            aria-label="Bulleted list"
            disabled={frozen}
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <ListIcon size={16} />
          </button>
          <LinkButton links={links} disabled={frozen} />
        </div>
        <EditorContent editor={editor} />
        <LinkLayer links={links} />
        {draft.signOff?.html && (
          <div className="lcr-draft-ending">{parseHtml(cleanEmailHtml(draft.signOff.html))}</div>
        )}
        <div className="lcr-compose-foot">
          {/* Kept only so the rep can copy the text: a save would end the edit, and the editor with it. */}
          {dirty && !awaited && (
            <button
              type="button"
              className="lcr-small-button"
              disabled={saving}
              onClick={() => void save()}
            >
              Save changes
            </button>
          )}
          <SaveStatus status={status} failed={failed} note={editorNote(held, awaited && dirty)} />
        </div>
        <DraftHintsPanel hints={hints} links={links} id={draftHintsId(step.id)} />
      </div>
      <DraftEnding
        draft={draft}
        plan={plan}
        step={step}
        options={signOffOptions}
        disabled={dirty || saving || held || awaited}
        onSaved={onSaved}
      />
    </>
  );
}

/**
 * Why the editor is not doing what it usually does: held while "Draft again" is asked, or saving
 * nothing while a new draft is written over text the rep has not saved. Null otherwise.
 */
function editorNote(held: boolean, keptUnsaved: boolean): string | null {
  if (held) return "Asking for a new draft. This one is held until the app answers.";
  if (keptUnsaved)
    return "A new draft is being written for this reply. Your unsaved edits are still here; copy them before it arrives.";
  return null;
}

/**
 * The editor's status: its own save status, and the note that says why it is held. A note stands in
 * for "Unsaved changes" but never for a failure: a save that failed, or a newer draft come in over
 * unsaved text, is still drawn as failed above it.
 */
function SaveStatus(props: { status: string; failed: boolean; note: string | null }) {
  return (
    <div className="lcr-save-status" role="status">
      {(props.failed || props.note === null) && (
        <div className={props.failed ? "lcr-save lcr-save-failed" : "lcr-save"}>{props.status}</div>
      )}
      {/* A sentence the rep needs, so it wraps and reads in the body's ink; it is not a failure. */}
      {props.note && <div className="lcr-save lcr-save-note">{props.note}</div>}
    </div>
  );
}
